import ServiceLogo from '../../resources/ServiceLogo.svg?react';
import {
  Box,
  Button,
  Container,
  Divider,
  Link,
  Paper,
  Stack,
  SxProps,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { AccountMenu } from '@ailex/react-components';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router';
import NavigationMenu from './NavigationMenu';
import { useTranslation } from 'react-i18next';
import { JaaRoute } from '../../utilities/Links';
import { useEffect, useMemo, useRef, useState } from 'react';
import SearchInput from '../ContentSearch/SearchInput';
import PredictiveSearchResultsDropdown from '../ContentSearch/PredictiveSearchResultsDropdown';
import CloseIcon from '@mui/icons-material/Close';
import { minSearchLength } from '../ContentSearch/SearchUtils';
import ContentTypeBar from './ContentTypeBar';
import { useAuth } from '../Auth/AuthProvider';

interface MenuLinkProps {
  to: string;
  variant?: 'default' | 'mobile';
  children: React.ReactNode;
}

function MenuLink({
  to,
  variant = 'default',
  children,
}: Readonly<MenuLinkProps>) {
  const location = useLocation();

  // Check if the current path starts with the `to` prop (indicating a partial match)
  let isActive = location.pathname.startsWith(to);

  if (to === '/') {
    // With root path, we need to check for exact match
    isActive = location.pathname === to;
  }

  let styles: SxProps = {
    color: isActive ? 'primary.main' : 'text.primary',
    fontSize: '1.125rem !important',
    whiteSpace: 'nowrap',
    lineHeight: '130%',
    fontWeight: 700,
  };

  if (variant === 'mobile') {
    styles = {
      ...styles,
      color: isActive ? 'primary.main' : 'text.secondary',
      fontSize: '1rem',
      paddingX: 2,
      paddingY: 1.125,
      lineHeight: '150%',
    };
  }

  return (
    <Link component={RouterLink} to={to} sx={styles} underline='hover'>
      {children}
    </Link>
  );
}

export default function Header() {
  const theme = useTheme();
  const { t } = useTranslation();
  const minimizeHeader = useMediaQuery(theme.breakpoints.down('md'));
  const minimizeSearchButton = useMediaQuery(theme.breakpoints.down('lg'));
  const showAccountMenu = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const [showSearch, setShowSearch] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [searchInput, setSearchInput] = useState('');
  const [showError, setShowError] = useState(false);
  const [showPredictiveResults, setShowPredictiveResults] = useState(false);
  const navigate = useNavigate();
  const identity = useAuth();

  function handleSearchClick() {
    // Toggle search visibility
    setShowSearch(!showSearch);
  }

  function onSubmit(search: string) {
    const value = inputRef.current?.value ?? '';

    if (value.length < minSearchLength) {
      setShowError(true);
      return;
    }

    setShowError(false);
    setSearchInput(search);
    navigate(`${JaaRoute.articleSearch}?value=${encodeURIComponent(search)}`);
  }

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    onSubmit(inputRef.current?.value ?? '');
  }

  function handleChange(search: string) {
    setShowError(false);
    setSearchInput(search);
  }

  function handleDropdownClickAway() {
    setShowPredictiveResults(false);
  }

  const isContentIndex = useMemo(() => {
    return location.pathname.startsWith(JaaRoute.contentIndex);
  }, [location]);

  // Handle showing predictive search results
  useEffect(() => {
    // Show predictive search results only when search input is long enough
    if (searchInput.length >= minSearchLength) {
      setShowPredictiveResults(true);
    } else {
      setShowPredictiveResults(false);
    }
  }, [searchInput]);

  useEffect(() => {
    setShowSearch(false);
  }, [location]);

  useEffect(() => {
    if (!showSearch) {
      setShowPredictiveResults(false);
      setSearchInput('');
      setShowError(false);
    }
  }, [showSearch]);

  const links = [
    {
      label: t('frontPage.name'),
      to: JaaRoute.frontPage,
    },
    {
      label: t('content.indexTitle'),
      to: JaaRoute.contentIndex,
    },
    {
      label: t('news.title'),
      to: JaaRoute.newsIndex,
    },
    {
      label: t('authors.title'),
      to: JaaRoute.authorListPage,
    },
    {
      label: t('regulationFeed.title'),
      to: JaaRoute.regulationIndex,
    },
  ];

  return (
    <>
      <Paper component='header' elevation={0} sx={{ zIndex: 1301 }}>
        <Container maxWidth='lg' disableGutters sx={{ paddingX: 2 }}>
          <Stack
            direction={'row'}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
              flexGrow: 1,
              height: { xs: '48px', md: '64px' },
            }}
          >
            <Stack
              direction='row'
              sx={{
                alignItems: 'center',
                height: '100%',
              }}
            >
              <Typography
                component={RouterLink}
                to={JaaRoute.frontPage}
                sx={{ color: 'text.primary', marginTop: 0.75 }}
                aria-label={t('service.title')}
              >
                <ServiceLogo
                  {...(minimizeHeader
                    ? {}
                    : { height: '32px', width: '187px' })}
                />
              </Typography>
              {!minimizeHeader && (
                <Box
                  sx={{
                    paddingY: 1.5,
                    paddingX: 2,
                    height: '100%',
                  }}
                >
                  <Divider orientation='vertical' />
                </Box>
              )}
              {!minimizeHeader && (
                <Stack direction='row' spacing={3}>
                  {links.map((link) => {
                    return (
                      <MenuLink key={'header-link-to-' + link.to} to={link.to}>
                        {link.label}
                      </MenuLink>
                    );
                  })}
                </Stack>
              )}
            </Stack>
            <Stack
              direction='row'
              sx={{
                alignItems: 'center',
                gap: minimizeHeader || minimizeSearchButton ? 0 : 2,
              }}
            >
              {minimizeSearchButton && (
                <Tooltip title={t('search.title')}>
                  <Link
                    component={RouterLink}
                    sx={{
                      color: 'text.secondary',
                      display: 'flex',
                      alignItems: 'center',
                      padding: 1.5,
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                    to={JaaRoute.articleSearch}
                  >
                    <SearchIcon fontSize='medium' />
                  </Link>
                </Tooltip>
              )}
              {!minimizeSearchButton && (
                <Button
                  variant='text'
                  onClick={handleSearchClick}
                  size='small'
                  sx={{
                    color: 'text.primary',
                    fontSize: '14px',
                  }}
                >
                  {showSearch ? t('search.close') : t('search.title')}
                  {showSearch ? (
                    <CloseIcon fontSize='small' sx={{ marginLeft: 0.5 }} />
                  ) : (
                    <SearchIcon fontSize='small' sx={{ marginLeft: 0.5 }} />
                  )}
                </Button>
              )}
              {showAccountMenu && <AccountMenu identityContext={identity} />}
              <NavigationMenu minimize={minimizeSearchButton} />
            </Stack>
          </Stack>
          {showSearch && !minimizeHeader && (
            <Stack
              component='form'
              noValidate
              autoComplete='off'
              onSubmit={handleSubmit}
              useFlexGap
              sx={{
                padding: 3,
                gap: 3,
              }}
            >
              <Box sx={{ display: 'flex', gap: 2 }}>
                <SearchInput
                  id='global-search'
                  onChange={handleChange}
                  onSubmit={onSubmit}
                  inputRef={inputRef}
                  error={showError}
                  helperText={
                    showError
                      ? t('search.searchTerm.minCharacters', {
                          count: minSearchLength,
                        })
                      : t('search.searchTerm.helperText')
                  }
                  dense
                />
                <Button
                  variant='text'
                  color='primary'
                  size='small'
                  sx={{ height: '40px', width: '160px' }}
                  component={RouterLink}
                  to={`${JaaRoute.articleSearch}?value=${encodeURIComponent(
                    searchInput,
                  )}`}
                >
                  {t('search.searchTerm.filteredSearch')}
                </Button>
              </Box>

              <PredictiveSearchResultsDropdown
                open={showPredictiveResults}
                anchorEl={inputRef.current?.parentElement ?? null}
                search={searchInput}
                onClickAway={handleDropdownClickAway}
              />
            </Stack>
          )}
        </Container>
        {minimizeHeader && (
          <>
            <Divider />
            <Stack
              direction='row'
              sx={{
                overflowX: 'scroll',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': {
                  display: 'none',
                },
              }}
            >
              {links.map((link) => {
                return (
                  <MenuLink
                    key={'header-link-to-' + link.to}
                    variant='mobile'
                    to={link.to}
                  >
                    {link.label}
                  </MenuLink>
                );
              })}
            </Stack>
          </>
        )}
        {isContentIndex && !minimizeHeader && (
          <>
            <Divider />
            <Container maxWidth='lg' disableGutters>
              <ContentTypeBar />
            </Container>
          </>
        )}
      </Paper>
      <Divider />
      {isContentIndex && minimizeHeader && <ContentTypeBar />}
    </>
  );
}
